import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Eye, Copy, Star, Plus } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { GET_ONBOARDING_VERSIONS } from '../../../graphql/queries';
import { SET_DEFAULT_ONBOARDING_VERSION } from '../../../graphql/mutations';

function OnboardingVersionsPage() {
  const { data, loading, refetch } = useQuery(GET_ONBOARDING_VERSIONS);
  const [setDefaultVersion, { loading: settingDefault }] = useMutation(SET_DEFAULT_ONBOARDING_VERSION);

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultVersion({ variables: { id } });
      await refetch();
    } catch (error) {
      console.error('Error setting default version:', error);
    }
  };

  return (
    <AdminLayout title="Onboarding Wizard Versions" description="Manage onboarding flow versions">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">All Versions</h2>
          <Button variant="default" size="sm" className="gap-1.5 bg-gray-900 hover:bg-gray-800" asChild>
            <Link to="/admin/onboarding/editor">
              <Plus className="w-3.5 h-3.5 shrink-0" aria-hidden />
              New Version
            </Link>
          </Button>
        </div>

        {loading ? (
          <div className="text-gray-500">Loading versions...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Steps
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.onboardingVersions?.map((version: any) => (
                  <tr key={version.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      v{version.version}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{version.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {version.isDefault && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{version.totalStepCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{version.requiredStepCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(version.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2 flex-wrap items-center">
                        <Button variant="outline" size="sm" className="gap-1.5 font-normal" asChild>
                          <Link to={`/admin/onboarding/${version.id}`}>
                            <Eye className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            View
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1.5 font-normal" asChild>
                          <Link to={`/admin/onboarding/editor/${version.id}`}>
                            <Copy className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            Clone
                          </Link>
                        </Button>
                        {!version.isDefault && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 font-normal text-green-700 border-green-200 hover:bg-green-50 hover:text-green-800"
                            onClick={() => handleSetDefault(version.id)}
                            disabled={settingDefault}
                          >
                            <Star className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            {settingDefault ? 'Setting…' : 'Make default'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default OnboardingVersionsPage;
